"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageTrademania2ReplaySchema } from "@/sanity/pages/PageTrademania2ReplayTemplate";
import { ToolsVisual } from "@/components/trademania-2/ToolsVisual";
import { PlatformVisual } from "@/components/trademania-2/CardVisuals";
import {
  Checklist,
  Emphasis,
  StepCard,
  StepCopy,
  StepSection,
  cardTitleClass,
  ctaPhoneLabelClass,
} from "@/components/trademania-2-replay/shared";

/** Step 1: free account, with the launch page's card 01 visual (chat · chart · journal). */
export function FreeStep({ data }: { data: PageTrademania2ReplaySchema }) {
  return (
    <StepSection
      chip={data?.freeChip}
      title={data?.freeTitle}>
      {/* Figma fixes this box at 573, the copy sits centred in it */}
      <StepCard className="lg:min-h-[71.625rem]">
        <StepCopy>
          <div className="flex flex-col gap-[2rem]">
            {data?.freeCardTitle && <h3 className={cn(cardTitleClass, "lg:w-[40.375rem]")}>{data.freeCardTitle}</h3>}
            {data?.freeCardText && (
              <p className="max-w-[41.125rem] font-geist text-[2rem] leading-[1.4] text-[#0B0C0D]/70 lg:max-w-[48.5rem]">
                {data.freeCardText}
              </p>
            )}
          </div>
          <Checklist items={data?.freeChecklist} />
          {data?.freeCTA && (
            <CTACornerButton
              data={data.freeCTA}
              className={ctaPhoneLabelClass}
            />
          )}
        </StepCopy>
        {/* card 01 board, taller as in the Figma slot (683 × 559 / 386 × 316) */}
        <ToolsVisual
          height={559}
          className="w-full rounded-[4px]"
        />
      </StepCard>
    </StepSection>
  );
}

/** Step 3: Pro discount, with the launch page's card 02 visual (MacBook over the glow). */
export function OfferStep({ data }: { data: PageTrademania2ReplaySchema }) {
  return (
    <StepSection
      chip={data?.offerChip}
      title={data?.offerTitle}
      className="gap-[3rem] pb-[5rem] lg:gap-[4rem] lg:pb-[12rem]">
      <StepCard>
        <StepCopy>
          {data?.offerCardTitle && (
            <h3 className={cn(cardTitleClass, "whitespace-pre-line lg:whitespace-pre")}>
              <Emphasis
                text={data.offerCardTitle}
                emphasis={(part) => <em>{part}</em>}
              />
            </h3>
          )}
          {data?.offerCardLead && (
            <p className="font-geist text-[2.25rem] leading-[1.3] font-medium text-[#0B0C0D] lg:w-[32.125rem]">
              {data.offerCardLead}
            </p>
          )}
          {data?.offerCardText && (
            <p className="font-geist text-[2rem] leading-[1.4] text-[#0B0C0D]/70 lg:w-[46.5rem]">{data.offerCardText}</p>
          )}
          <Checklist items={data?.offerChecklist} />
          {data?.offerCTA && <CTACornerButton data={data.offerCTA} />}
        </StepCopy>
        {/* card 02 board stretched to the Figma slot (683 × 560 / 386 × 316): the glow grows, the MacBook stays */}
        <PlatformVisual
          image={data?.offerImage}
          video={data?.offerVideo}
          height={520}
          className="w-full rounded-[4.305px]"
        />
      </StepCard>
    </StepSection>
  );
}
